import React, { useState, useMemo, useCallback } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import PackersNumpad from "@/components/packers/PackersNumpad";
import PackersToolbar from "@/components/packers/PackersToolbar";
import PackerOrderCard from "@/components/packers/PackerOrderCard";
import { usePackersList, useMarkPrinted, useMarkPacked, usePrintLabel } from "@/hooks/usePackers";

const Packers = () => {
  const [numpadFilter, setNumpadFilter] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, isLoading, refetch } = usePackersList({ limit: 200 });
  const markPrinted = useMarkPrinted();
  const markPacked = useMarkPacked();
  const printLabel = usePrintLabel();

  const orders = data?.data || [];
  const meta = data?.meta || { total: 0, limit: 0, offset: 0, packed_today: 0 };

  const filteredOrders = useMemo(() => {
    if (!numpadFilter) return orders;
    return orders.filter((o) => o.ordernumber.endsWith(numpadFilter));
  }, [orders, numpadFilter]);

  const toggleSelect = useCallback((ordernumber: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(ordernumber)) next.delete(ordernumber);
      else next.add(ordernumber);
      return next;
    });
  }, []);

  const selectAll = () => {
    if (selected.size === filteredOrders.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredOrders.map((o) => o.ordernumber)));
    }
  };

  const printSelected = () => {
    if (selected.size === 0) return;
    const ordernumbers = [...selected];
    printLabel.mutate(ordernumbers, {
      onSuccess: () => {
        markPrinted.mutate(ordernumbers, { onSuccess: () => setSelected(new Set()) });
      },
    });
  };

  const handlePrintLabel = (ordernumber: string) => {
    printLabel.mutate([ordernumber]);
  };

  const printNew = () => {
    const unprinted = filteredOrders.filter((o) => !o.is_printed).map((o) => o.ordernumber);
    if (unprinted.length === 0) return;
    markPrinted.mutate(unprinted);
  };

  const handleMarkPacked = (ordernumber: string) => {
    markPacked.mutate([ordernumber]);
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(ordernumber);
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a1f3d] to-[#0f1221] p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-white text-xl font-bold">Packers</h1>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => refetch()}
          className="text-white/60 hover:text-white hover:bg-white/10"
        >
          <RefreshCw className="w-5 h-5" />
        </Button>
      </div>

      {/* Numpad */}
      <PackersNumpad filter={numpadFilter} onFilterChange={setNumpadFilter} />

      {/* Toolbar */}
      <PackersToolbar
        total={meta.total}
        packedToday={meta.packed_today}
        selectedCount={selected.size}
        onSelectAll={selectAll}
        onPrintSelected={printSelected}
        onPrintNew={printNew}
        isPrinting={markPrinted.isPending || printLabel.isPending}
      />

      {/* Orders */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-white/40 animate-spin" />
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="text-center py-20 text-white/40">
          {numpadFilter
            ? `Geen orders eindigend op ...${numpadFilter}`
            : "Geen orders beschikbaar"}
        </div>
      ) : (
        <div className="space-y-3 pb-20">
          {filteredOrders.map((order) => (
            <PackerOrderCard
              key={order.ordernumber}
              order={order}
              selected={selected.has(order.ordernumber)}
              onToggleSelect={toggleSelect}
              onMarkPacked={handleMarkPacked}
              onPrintLabel={handlePrintLabel}
              isPacking={markPacked.isPending}
              isPrintingLabel={printLabel.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Packers;
